import React from "react";
import { Box, Button, Typography, Card, CardContent, Container, Tooltip, IconButton, TextField, Stack, Dialog, DialogTitle, DialogContent, DialogActions } from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import { useKnowledgeBaseData } from "./KnowledgeBaseData";
import Grid from '@mui/material/Grid2';
import MiniDrawer from "../../components/MiniDrawer.js";
import { useTheme } from '@mui/material/styles';
import PopupMenu from "../PopupMenu/PopupMenu.js";
import { UploadFile as UploadFileIcon, Download, Delete, DeleteOutline } from "@mui/icons-material";
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import Loader from "../../loader.js";
import { useNavigate } from 'react-router-dom';


const KnowledgeBase = () => {
    const {     
      categories, 
      handleAddCategory, 
      handleAddDocuments, 
      setAlert, 
      handleDeleteDocument,
      newCategory,
      setNewCategory,
      newDocument,
      setNewDocument,
      categoryTitle,
      setCategoryTitle,
      categoryDescription,
      setCategoryDescription,
      formSubmitted,
      setFormSubmitted,
      categoryID,
      setCategoryID,
      files,
      setFiles,
      viewFiles,
      setViewFiles,
      isViewingFiles,
      setIsViewingFiles,
      handleFileUpload,
      handleDrop,
      handleDownload,
      handleDelete,
      loading,
      setLoading,
      downloadFile,
      subscriptionActiveStatus,
      handleDeleteCategory,
      userDetails
    } 
    = useKnowledgeBaseData();
    
    const navigate = useNavigate();
    const theme = useTheme();

    const [deleteDialogOpen, setDeleteDialogOpen] = React.useState(false);
    const [categoryToDelete, setCategoryToDelete] = React.useState(null);

    const handleDeleteClick = (category) => {
        setCategoryToDelete(category);
        setDeleteDialogOpen(true);
    };

    const handleDeleteConfirm = async () => {
        if (categoryToDelete) {
            setLoading(true);
            await handleDeleteCategory(categoryToDelete.id);
            setLoading(false);
        }
        setDeleteDialogOpen(false);
        setCategoryToDelete(null);
    };

    const handleDeleteCancel = () => {
        setDeleteDialogOpen(false);
        setCategoryToDelete(null);
    };

    return (
        <Container disableGutters maxWidth={false}>
            <Grid container sx={{overflow: 'auto', minHeight: '100vh', minWidth: '100vw'}}>
              <Grid size={{xs: 1, md: 0}}>
              <MiniDrawer />
              </Grid>
              <Grid size="grow">
              <Grid container spacing={5} sx={{ backgroundColor: '#F8F9FA', display: 'flex', flexDirection: 'column',  width: '100%', height: '100%' }}>
                  <Grid size={12}>
                      <div style={{ padding: '24px 10vw' }}>
                          <Grid container spacing={2} sx={{ flexDirection: 'column' }}>
                              <Grid
                                  sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2 }}
                              >
                                  <Typography variant="h4" sx={{marginRight: '20px'}}>Knowledge Base</Typography>
                                  <Button onClick={() => { setNewCategory(true); setFormSubmitted(false); setCategoryTitle(""); setCategoryDescription("")}} variant="outlined" startIcon={<AddIcon sx={{color: '#fff'}}/>} sx={{marginRight: '20px', borderRadius: 30, backgroundColor: '#29C0BB', border: 0}}>
                                  <Typography variant="button" sx={{color: '#FFFFFF'}}>Add Category</Typography>
                                  </Button>
                              </Grid>
                              <Grid container spacing={3} sx={{ display: "flex", justifyContent: "flex-start", alignItems: "stretch" }}>
                                  {categories.map((category, index) => (
                                      <Grid size={{ xs: 12, sm: 6, md: 3 }} key={index}>
                                          <Card sx={{ padding: 2, backgroundColor: "#FFFFFF", borderRadius: 5, height: '100%', boxShadow: '0px 0px 15px rgba(0, 0, 0, 0.2)' }}> 
                                              <CardContent sx={{ p:0, '&:last-child': { pb: 0 }, display: 'flex', flexDirection: 'column', justifyContent: 'space-between', height: '100%' }}>
                                                <div>
                                                  <Box
                                                    sx={{
                                                      display: 'flex',
                                                      alignItems: 'center',
                                                      justifyContent: 'space-between',
                                                      gap: 1,
                                                      width: '100%',
                                                      minWidth: 0,
                                                    }}
                                                  >
                                                    <Typography
                                                      variant="h6"
                                                      sx={{
                                                        flex: 1,
                                                        minWidth: 0,
                                                        whiteSpace: 'nowrap',
                                                        overflow: 'hidden',
                                                        textOverflow: 'ellipsis',
                                                      }}
                                                      title={category.title}
                                                    >
                                                      {category.title}
                                                    </Typography>
                                                    <Tooltip title="Delete category">
                                                      <IconButton
                                                        onClick={() => handleDeleteClick(category)}
                                                        size="medium"
                                                        sx={{
                                                          flexShrink: 0,
                                                          color: '#ff4444',
                                                          '&:hover': { backgroundColor: 'rgba(255, 68, 68, 0.1)' },
                                                        }}
                                                      >
                                                        <DeleteOutline fontSize="small" />
                                                      </IconButton>
                                                    </Tooltip>
                                                  </Box>
                                                  <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
                                                      {category.description}
                                                  </Typography>
                                                </div>
                                                <div>
                                                  <Tooltip title="View documents">
                                                    <IconButton 
                                                      onClick={() => { 
                                                        if(category.documents.length > 0) {
                                                          setViewFiles(category.documents);
                                                          setCategoryID(category.id); 
                                                          setIsViewingFiles(true);
                                                        } else {
                                                          setAlert('No documents uploaded.', 'warning');
                                                        }
                                                      }} 
                                                        style={{ marginBottom: '10px', borderRadius: 30, backgroundColor: '#F8F9FA', padding: '5px 10px', cursor: 'pointer' }}>
                                                      <Typography variant="body2" sx={{ color: "#29C0BB", fontSize: '18px' }}>{category.documents.length} documents added</Typography>
                                                    </IconButton>
                                                  </Tooltip>
                                                  <Tooltip title="Add documents">
                                                      <IconButton
                                                        onClick={() => { setNewDocument(true); setFiles([]); setCategoryID(category.id); }} 
                                                        style={{ 
                                                          borderRadius: 30, backgroundColor: '#F8F9FA', padding: '5px 10px', color: "#29C0BB", cursor: 'pointer', 
                                                          display: 'flex',
                                                          flexDirection: { xs: 'column', md: 'row' }
                                                          }} >
                                                          <AddIcon />
                                                          <Typography variant="body2" sx={{ color: '#29C0BB', cursor: 'pointer', marginLeft: '2px', fontSize: '18px' }}>Add documents</Typography>
                                                      </IconButton>
                                                  </Tooltip>
                                                </div>
                                              </CardContent>
                                          </Card>
                                      </Grid>
                                  ))}
                              </Grid>
                          </Grid>
                      </div>
                  </Grid>
                </Grid>
              </Grid>

              <Dialog
                open={deleteDialogOpen}
                onClose={handleDeleteCancel}
                maxWidth="sm"
                fullWidth
              >
                <DialogTitle sx={{ textAlign: 'center', pt: 3 }}>
                  <Box
                    sx={{
                      width: '60px',
                      height: '60px',
                      borderRadius: '50%',
                      backgroundColor: '#FFE6E6',
                      display: 'flex',
                      justifyContent: 'center',
                      alignItems: 'center',
                      margin: '0 auto 16px auto',
                    }}
                  >
                    <DeleteOutline sx={{ fontSize: '32px', color: '#FF6B6B' }} />
                  </Box>
                  <Typography variant="h5" sx={{ fontWeight: 'bold', color: '#333333' }}>
                    Delete Category
                  </Typography>
                </DialogTitle>
                <DialogContent sx={{ textAlign: 'center', pb: 2 }}>
                  <Typography variant="body1" sx={{ color: '#666666', lineHeight: 1.6 }}>
                    Are you sure you want to delete the category{' '}
                    <strong>"{categoryToDelete?.title}"</strong>?
                  </Typography>
                  <Typography variant="body2" sx={{ color: '#999999', mt: 2 }}>
                    This action will permanently delete the category and all its associated documents. 
                    This cannot be undone.
                  </Typography>
                </DialogContent>
                <DialogActions sx={{ justifyContent: 'center', pb: 3 }}>
                  <Button
                    onClick={handleDeleteCancel}
                    sx={{
                      borderRadius: '30px',
                      padding: '10px 24px',
                      textTransform: 'none',
                      color: '#29C0BB',
                      border: '1px solid #29C0BB',
                      '&:hover': {
                        backgroundColor: 'rgba(41, 192, 187, 0.1)',
                      },
                    }}
                  >
                    Cancel
                  </Button>
                  <Button
                    onClick={handleDeleteConfirm}
                    sx={{
                      borderRadius: '30px',
                      padding: '10px 24px',
                      textTransform: 'none',
                      backgroundColor: '#FF6B6B',
                      color: '#FFFFFF',
                      ml: 2,
                      '&:hover': {
                        backgroundColor: '#FF5252',
                      },
                    }}
                  >
                    Delete
                  </Button>
                </DialogActions>
              </Dialog>
                {
                  newCategory && (
                    <PopupMenu>
                      <Typography variant="h6">Add New Category</Typography>
                      <Grid container spacing={2}>
                        <Grid size={12}>
                          <TextField
                            required
                            fullWidth
                            label="Title"
                            variant="outlined"
                            focused
                            value={categoryTitle}
                            onChange={(e) => setCategoryTitle(e.target.value)}
                            placeholder="Title" 
                            sx={{ marginTop: '20px', marginBottom: '16px', backgroundColor: '#f5f5f5' }} 
                            error={formSubmitted && !categoryTitle}
                            helperText={formSubmitted && !categoryTitle && "Category Title is required"}
                          />
                        </Grid>
                        <Grid size={12}>
                          <TextField
                            required
                            fullWidth
                            label="Description"
                            variant="outlined"
                            focused
                            value={categoryDescription}
                            onChange={(e) => setCategoryDescription(e.target.value)}
                            placeholder="Description" 
                            sx={{ marginTop: '20px', marginBottom: '16px', backgroundColor: '#f5f5f5' }}
                            error={formSubmitted && !categoryDescription}
                            helperText={formSubmitted && !categoryDescription && "Category Description is required"}
                          />
                        </Grid>
                        <Grid size={12}>
                          <Stack direction="row" alignItems="center" justifyContent="flex-end" spacing={2}>                   
                            <Box onClick={() => {setNewCategory(false)}} sx={{ width: '80px', textAlign: 'center', borderRadius: 30, backgroundColor: '#F8F9FA', padding: '5px 10px', color: "#29C0BB", cursor: 'pointer'}}>
                              Close
                            </Box>  
                            <Box onClick={() => { 
                                setFormSubmitted(true);
                                if (!categoryTitle || !categoryDescription) {
                                  setAlert('Please fill in all required fields.', 'error');
                                  return;
                                }
                                handleAddCategory(categoryTitle, categoryDescription); 
                                setNewCategory(false); 
                                setAlert('Category added successfully.', 'success');
                              }} 
                              sx={{ width: '80px', textAlign: 'center', borderRadius: 30, backgroundColor: '#29C0BB', padding: '5px 10px', color: "#ffffff", cursor: 'pointer'}}>
                              Confirm
                            </Box>  
                          </Stack>
                        </Grid>
                      </Grid>
                    </PopupMenu> 
                  )
                }
                {
                  newDocument && (
                    <PopupMenu>
                      <Typography variant="h6">Attach Documents</Typography>
                        <Grid container spacing={2}>
                          <Grid size={12}>
                            <Box
                              sx={{
                                  border: "2px dashed #ccc",
                                  borderRadius: 2,
                                  padding: 3,
                                  textAlign: "center",
                                  cursor: "pointer",
                                  mb: 2,
                                  mt: 2,
                                  position: "relative",
                              }}
                              onClick={() => document.getElementById("file-input").click()}
                              onDrop={handleDrop}
                              onDragOver={(event) => event.preventDefault()}
                              >
                              <UploadFileIcon sx={{ fontSize: 40, color: "#ccc" }} />
                              <Typography sx={{ mt: 1 }}>Drag and Drop files or Browse</Typography>
                              <input
                                  id="file-input"
                                  type="file"
                                  multiple
                                  onChange={handleFileUpload}
                                  style={{
                                  display: "none",
                                  }}
                              />
                              </Box>
                              {files.map((file, index) => (
                                <Box
                                  key={index}
                                  sx={{
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "space-between",
                                    backgroundColor: "white",
                                    padding: "8px",
                                    borderRadius: "4px",
                                    boxShadow: "0px 2px 4px rgba(0, 0, 0, 0.1)",
                                    marginBottom: "8px",
                                  }}
                                >
                                  <Typography variant="body1" sx={{ flex: 1, marginRight: 2 }}>
                                    {file.name}
                                  </Typography>
                                  <IconButton
                                    color="primary"
                                    onClick={() => handleDownload(file)}
                                    sx={{ marginRight: 1 }}
                                  >
                                    <Download />
                                  </IconButton>
                                  <IconButton
                                    color="error"
                                    onClick={() => handleDelete(index)}
                                  >
                                    <Delete />
                                  </IconButton>
                                </Box>
                              ))}
                          </Grid>
                          <Grid size={12}>
                            <Stack direction="row" alignItems="center" justifyContent="flex-end" spacing={2}>                   
                              <Box onClick={() => {setNewDocument(false)}} sx={{ width: '80px', textAlign: 'center', borderRadius: 30, backgroundColor: '#F8F9FA', padding: '5px 10px', color: "#29C0BB", cursor: 'pointer'}}>
                                Close
                              </Box>  
                              <Box onClick={() => { 
                                  setFormSubmitted(true);
                                  handleAddDocuments(categoryID, files); 
                                  setNewDocument(false); 
                                  setLoading(true);
                                }} 
                                sx={{ width: '80px', textAlign: 'center', borderRadius: 30, backgroundColor: '#29C0BB', padding: '5px 10px', color: "#ffffff", cursor: 'pointer'}}>
                                Confirm
                              </Box>  
                            </Stack>
                          </Grid>
                        </Grid>
                    </PopupMenu>
                  )
                }
                {
                  isViewingFiles && (
                    <PopupMenu>
                      <Typography variant="h6">Document List</Typography>
                        <Grid container spacing={2}>
                          <Grid size={12}>
                              {viewFiles.map((file, index) => (
                                <Box
                                  key={index}
                                  sx={{
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "space-between",
                                    backgroundColor: "white",
                                    padding: "8px",
                                    borderRadius: "4px",
                                    boxShadow: "0px 2px 4px rgba(0, 0, 0, 0.1)",
                                    marginBottom: "8px",
                                  }}
                                >
                                  <Typography variant="body1" sx={{ flex: 1, marginRight: 2 }}>
                                    {file}
                                  </Typography>
                                  <Stack direction="row" alignItems="center" justifyContent="flex-end" spacing={2}>
                                    <IconButton
                                      color="primary"
                                      onClick={() => downloadFile(categoryID, file)}
                                    >
                                      <FileDownloadIcon />
                                    </IconButton>
                                    <IconButton
                                      color="error"
                                      onClick={() => { handleDeleteDocument(categoryID, file); setLoading(true); }}
                                    >
                                      <Delete />
                                    </IconButton>
                                  </Stack>
                                </Box>
                              ))}
                          </Grid>
                          <Grid size={12}>
                            <Stack direction="row" alignItems="center" justifyContent="flex-end" spacing={2}>                   
                              <Box onClick={() => {setIsViewingFiles(false);}} sx={{ width: '80px', textAlign: 'center', borderRadius: 30, backgroundColor: '#F8F9FA', padding: '5px 10px', color: "#29C0BB", cursor: 'pointer'}}>
                                Close
                              </Box>   
                            </Stack>
                          </Grid>
                        </Grid>
                    </PopupMenu>
                  )
                }
                {
                    loading && (
                      <PopupMenu>
                        <Stack direction="column" alignItems="center" justifyContent="center" spacing={2}>
                          <Typography variant="h6">{'Updating Documents'}</Typography>
                          <Loader />
                        </Stack>
                      </PopupMenu>)
                }
            </Grid>
            {(subscriptionActiveStatus !== false && userDetails && !userDetails.mode) && (
              <Box
                sx={{
                  position: 'fixed',
                  top: 0,
                  left: 0,
                  width: '100vw',
                  height: '100vh',
                  backgroundColor: 'rgba(0, 0, 0, 0.3)',
                  backdropFilter: 'blur(8px)',
                  WebkitBackdropFilter: 'blur(8px)',
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  zIndex: 9999,
                }}
              >
                <Box
                  sx={{
                    backgroundColor: '#FFFFFF',
                    borderRadius: '16px',
                    padding: '40px',
                    maxWidth: '500px',
                    width: '90%',
                    textAlign: 'center',
                    boxShadow: '0px 12px 32px rgba(0, 0, 0, 0.2)',
                    border: '2px solid #29C0BB',
                  }}
                >
                  <Box
                    sx={{
                      width: '80px',
                      height: '80px',
                      borderRadius: '50%',
                      backgroundColor: '#E0F7FA',
                      display: 'flex',
                      justifyContent: 'center',
                      alignItems: 'center',
                      margin: '0 auto 20px auto',
                    }}
                  >
                    <Typography
                      sx={{
                        fontSize: '36px',
                        color: '#29C0BB',
                      }}
                    >
                      ⚙️
                    </Typography>
                  </Box>
                  
                  <Typography
                    variant="h5"
                    sx={{
                      fontWeight: 'bold',
                      color: '#333333',
                      marginBottom: '16px',
                    }}
                  >
                    Profile Setup Required
                  </Typography>
                  
                  <Typography
                    variant="body1"
                    sx={{
                      color: '#666666',
                      marginBottom: '32px',
                      lineHeight: 1.6,
                    }}
                  >
                    Your profile setup is incomplete. To continue using the application, please select your User Mode in Profile Settings.
                  </Typography>
                  
                  <Stack direction="row" spacing={2} justifyContent="center">
                    <Button
                      variant="contained"
                      sx={{
                        backgroundColor: '#29C0BB',
                        color: '#FFFFFF',
                        borderRadius: '30px',
                        padding: '12px 24px',
                        textTransform: 'none',
                        fontWeight: 'bold',
                        '&:hover': {
                          backgroundColor: '#259B96',
                        },
                      }}
                      onClick={() => {
                        navigate('/settings');
                      }}
                    >
                      Go to Settings
                    </Button>
                  </Stack>
                </Box>
              </Box>
            )}
            {subscriptionActiveStatus === false && (
              <Box
                sx={{
                  position: 'fixed',
                  top: 0,
                  left: 0,
                  width: '100vw',
                  height: '100vh',
                  backgroundColor: 'rgba(0, 0, 0, 0.3)',
                  backdropFilter: 'blur(8px)',
                  WebkitBackdropFilter: 'blur(8px)',
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  zIndex: 9999,
                }}
              >
                <Box
                  sx={{
                    backgroundColor: '#FFFFFF',
                    borderRadius: '16px',
                    padding: '40px',
                    maxWidth: '500px',
                    width: '90%',
                    textAlign: 'center',
                    boxShadow: '0px 12px 32px rgba(0, 0, 0, 0.2)',
                    border: '2px solid #FF6B6B',
                  }}
                >
                  <Box
                    sx={{
                      width: '80px',
                      height: '80px',
                      borderRadius: '50%',
                      backgroundColor: '#FFE6E6',
                      display: 'flex',
                      justifyContent: 'center',
                      alignItems: 'center',
                      margin: '0 auto 20px auto',
                    }}
                  >
                    <Typography
                      sx={{
                        fontSize: '36px',
                        color: '#FF6B6B',
                      }}
                    >
                      ⚠️
                    </Typography>
                  </Box>
                  
                  <Typography
                    variant="h5"
                    sx={{
                      fontWeight: 'bold',
                      color: '#333333',
                      marginBottom: '16px',
                    }}
                  >
                    Subscription Inactive
                  </Typography>
                  
                  <Typography
                    variant="body1"
                    sx={{
                      color: '#666666',
                      marginBottom: '32px',
                      lineHeight: 1.6,
                    }}
                  >
                    Your subscription has expired and access to our platform is currently suspended. 
                    To continue using our services and manage your patients, please reactivate your subscription from the settings.
                  </Typography>
                  
                  <Stack direction="row" spacing={2} justifyContent="center">
                    <Button
                      variant="contained"
                      sx={{
                        backgroundColor: '#29C0BB',
                        color: '#FFFFFF',
                        borderRadius: '30px',
                        padding: '12px 24px',
                        textTransform: 'none',
                        fontWeight: 'bold',
                        '&:hover': {
                          backgroundColor: '#259B96',
                        },
                      }}
                      onClick={() => {
                        // Navigate to settings page - you might need to import useNavigate from react-router-dom
                        navigate('/settings');
                      }}
                    >
                      Subscribe Now
                    </Button>
                  </Stack>
                </Box>
              </Box>
            )}
        </Container>
    );
};

export default KnowledgeBase;